<script lang="ts" setup>
import CitySelect from '@/components/shared/base_mailing/form/CitySelect.vue'
import StateSelect from '@/components/shared/base_mailing/form/StateSelect.vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatNumber, formatText } from '@/helpers/format-text'
import { formatZipcode } from '@/helpers/format-zipcode'
import { states } from '@/helpers/states'
import { FormSchema } from '@/pages/carts/show.vue'
import { Loader2 } from 'lucide-vue-next'
import { computed, inject, ref, watch, type Ref } from 'vue'
const { isLoading, form } = defineProps<{
    form: FormSchema
    isLoading: boolean
}>()

const emit = defineEmits(['searchAddressByZipCode'])

const disabledButton = computed(() => {
    return !form.zip_code || form.zip_code.length != 9 || isLoading
})

function normalizeStateToRegionCode(value: string) {
    if (!value) return ''

    const normalizedValue = value.trim().toLowerCase()
    const stateByCode = states.find((state) => state.region_code.toLowerCase() === normalizedValue)

    if (stateByCode) return stateByCode.region_code

    const stateByName = states.find((state) => state.name.toLowerCase() === normalizedValue)

    if (stateByName) return stateByName.region_code

    return value
}

watch(
    () => form.state,
    (currentState) => {
        const normalizedState = normalizeStateToRegionCode(currentState)

        if (normalizedState && normalizedState !== currentState) {
            form.state = normalizedState
        }

        if (normalizedState && form.region_code !== normalizedState) {
            form.region_code = normalizedState
        }
    },
    { immediate: true },
)

watch(
    () => form.region_code,
    (currentRegionCode) => {
        const normalizedRegionCode = normalizeStateToRegionCode(currentRegionCode)

        if (normalizedRegionCode && normalizedRegionCode !== currentRegionCode) {
            form.region_code = normalizedRegionCode
        }

        if (normalizedRegionCode && form.state !== normalizedRegionCode) {
            form.state = normalizedRegionCode
        }
    },
)

const onStateChange = (value: string) => {
    if (form.state === value) return

    form.state = value
    form.region_code = value
    form.city = ''
}

const storedCity = inject<Ref<string>>('storedCity', ref(''))

const setStoredCity = (value: string) => {
    storedCity.value = value
}
</script>

<template>
    <div class="grid gap-x-2 gap-y-6 md:grid-cols-2 lg:gap-x-8" v-if="!form.has_foreign_doc || form.billing_type === 'company'">
        <div class="flex flex-col gap-2">
            <Label required for="zip_code">{{ $t('Zipcode') }} </Label>
            <div class="flex gap-1.5">
                <Input
                    id="zip_code"
                    type="text"
                    :placeholder="$t('00.000-00')"
                    v-model="form.zip_code"
                    @input="(e: Event) => (form.zip_code = formatZipcode((e.target as HTMLInputElement).value))"
                />
                <Button class="w-full max-w-36" type="button" @click.prevent="emit('searchAddressByZipCode')" :disabled="disabledButton">
                    <template v-if="!isLoading">
                        {{ $t('Search') }}
                    </template>
                    <template v-else>
                        <Loader2 class="animate-spin" />
                        <span class="sr-only">{{ $t('Searching') }}</span>
                    </template>
                </Button>
            </div>

            <p v-if="form.errors.zip_code" class="text-destructive mt-px text-xs">{{ $t(form.errors.zip_code) }}</p>
        </div>

        <div class="flex flex-col gap-2">
            <Label required for="street">{{ $t('Address') }} </Label>
            <Input id="street" type="text" v-model="form.street" />
            <p v-if="form.errors.street" class="text-destructive mt-px text-xs">{{ $t(form.errors.street) }}</p>
        </div>

        <div class="flex flex-col gap-2">
            <Label required for="number">{{ $t('Number') }} </Label>
            <Input
                id="number"
                type="text"
                v-model="form.number"
                @input="(e: Event) => (form.number = formatNumber((e.target as HTMLInputElement).value))"
            />
            <p v-if="form.errors.number" class="text-destructive mt-px text-xs">{{ $t(form.errors.number) }}</p>
        </div>

        <div class="flex flex-col gap-2">
            <Label for="additional_info">{{ $t('Complement') }} </Label>
            <Input id="additional_info" type="text" v-model="form.additional_info" />
            <p v-if="form.errors.additional_info" class="text-destructive mt-px text-xs">{{ $t(form.errors.additional_info) }}</p>
        </div>

        <div class="flex flex-col gap-2">
            <Label required for="district">{{ $t('Neighborhood') }} </Label>
            <Input id="district" type="text" v-model="form.district" />
            <p v-if="form.errors.district" class="text-destructive mt-px text-xs">{{ $t(form.errors.district) }}</p>
        </div>

        <StateSelect
            :model-value="form.state"
            :has-foreign-doc="false"
            @update:model-value="(value) => onStateChange(value as string)"
            :error="form.errors.state ? $t(form.errors.state) : ''"
        />

        <CitySelect
            v-model="form.city"
            :state="form.state"
            :has-foreign-doc="false"
            :error="form.errors.city ? $t(form.errors.city) : ''"
            :stored-city="storedCity"
            @update:stored-city="setStoredCity"
        />

        <div class="flex flex-col gap-2">
            <Label required for="country">{{ $t('Country') }} </Label>
            <Input
                id="country"
                type="text"
                v-model="form.country"
                @input="(e: Event) => (form.country = formatText((e.target as HTMLInputElement).value))"
            />
            <p v-if="form.errors.country" class="text-destructive mt-px text-xs">{{ $t(form.errors.country) }}</p>
        </div>
    </div>
</template>
